import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Carousel } from 'react-responsive-carousel';
import { CustomHtmlSection, CustomHtmlStyles } from 'components/commons/CustomHtml';
import MarketingNewsletter from 'components/commons/MarketingNewsletter';
import { isSlideImage, useRenderableHeroSliders } from 'helpers/sliders';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import './HomeD5.scss';

/* ---------- Carrusel horizontal de productos (NO es el hero) ---------- */
const RowCarousel = ({ items = [], renderItem, ariaLabel }) => {
  const trackRef = useRef(null);

  const scrollByDir = (dir) => {
    const node = trackRef.current;
    if (!node) return;
    const amount = Math.max(node.clientWidth * 0.9, 280);
    node.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  if (!items?.length) return null;

  return (
    <div className="row-carousel" role="region" aria-label={ariaLabel}>
      <button type="button" className="row-nav prev" aria-label="Anterior" onClick={() => scrollByDir(-1)}>‹</button>
      <div className="row-track" ref={trackRef}>
        {items.map((it, idx) => (
          <div className="row-item" key={it?.id ?? idx}>
            {renderItem(it)}
          </div>
        ))}
      </div>
      <button type="button" className="row-nav next" aria-label="Siguiente" onClick={() => scrollByDir(1)}>›</button>
    </div>
  );
};

/* ---------- Grilla vertical de productos (sin carrusel) ---------- */
const ProductsGrid = ({ items = [], renderItem, ariaLabel, layout = 'grid', limit = null }) => {
  if (!items?.length) return null;
  const visibleItems = limit ? items.slice(0, limit) : items;
  return (
    <div className={`products-grid products-grid--${layout}`} role="region" aria-label={ariaLabel}>
      {visibleItems.map((it, idx) => (
        <div className="products-grid__item" key={it?.id ?? idx}>
          {renderItem(it)}
        </div>
      ))}
    </div>
  );
};

/* ---------- helpers para carteles ---------- */
const getCatCode = (it) =>
  it?.category?.code ?? it?.category_code ?? it?.categoryCode ?? it?.codeCategory ?? it?.code;

const getSubCode = (it) =>
  it?.subcategory?.code ?? it?.subCategory?.code ?? it?.subcategory_code ?? it?.subCategoryCode;

const getBannerImage = (it) => it?.image ?? it?.imageHome ?? it?.banner ?? it?.url ?? '';
const getBannerImageMobile = (it) => it?.imageMobile ?? getBannerImage(it);
const getSlideMedia = (slide, onReady, onFailed) => (
  isSlideImage(slide) ? (
    <img
      src={slide.image}
      alt={slide.text || 'Portada'}
      loading="eager"
      decoding="async"
      onLoad={() => onReady(slide)}
      onError={() => onFailed(slide)}
    />
  ) : (
    <video
      src={slide.image}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      aria-label={slide.text || 'Video'}
      onLoadedData={() => onReady(slide)}
      onError={() => onFailed(slide)}
    />
  )
);

/* ---------- Componente principal ---------- */
const HomeD5 = ({
  cartel = [],
  sliders = [],
  categories = [],
  categoriesHome = [],
  mostSelled = [],
  featuredProducts = [],
  offers = [],
  marcasHome = [],
  renderItem,
  disableProductCarousel = false,
  heroCarouselProps = null,
  customHtml = null,
  variant = '',
  newsletter = '',
  setNewsletter,
  submitNewsletter,
  loadingNewsletter = false,
  client = null,
  presentation = null,
}) => {
  const heroMode = presentation?.hero || 'carousel';
  const productsMode = presentation?.products || 'grid';
  const productLimit = presentation?.productLimit || 8;
  const {
    heroSliders,
    markHeroSlideReady,
    markHeroSlideFailed,
  } = useRenderableHeroSliders(sliders);

  const redirectCartel = (i) => {
    const it = cartel?.[i];
    if (!it) return '/productos';
    const cat = getCatCode(it);
    const sub = getSubCode(it);
    if (cat && sub) return `/productos/${cat}/${sub}`;
    if (cat) return `/productos/${cat}`;
    return '/productos';
  };

  const renderHeroBlock = () => {
    if (!heroSliders.length) return null;

    if (heroMode === 'carousel' && heroSliders.length === 1) {
      const slide = heroSliders[0];
      return (
        <section className="hero-main-full hero-carousel">
          <div className="hero-image">
            <div className="hero-slide">
              {getSlideMedia(slide, markHeroSlideReady, markHeroSlideFailed)}
            </div>
          </div>
        </section>
      );
    }

    if (heroMode === 'carousel') {
      return (
        <section className="hero-main-full hero-carousel">
          <div className="hero-image">
            <Carousel
              autoPlay
              infiniteLoop
              showThumbs={false}
              showStatus={false}
              emulateTouch
              swipeable
              {...(heroCarouselProps || {})}
            >
              {heroSliders.map((slide) => (
                <div key={slide.id || slide.image} className="hero-slide">
                  {getSlideMedia(slide, markHeroSlideReady, markHeroSlideFailed)}
                </div>
              ))}
            </Carousel>
          </div>
        </section>
      );
    }

    const visibleSlides = heroSliders.slice(0, heroMode === 'mosaic' ? 3 : (heroMode === 'split' ? 2 : 1));

    return (
      <section
        className={`hero-main-full hero-carousel hero-main-full--static hero-main-full--${heroMode}`}
      >
        <div className="hero-image">
          {visibleSlides.map((slide) => (
            <div key={slide.id || slide.image} className="hero-slide">
              {getSlideMedia(slide, markHeroSlideReady, markHeroSlideFailed)}
            </div>
          ))}
        </div>
      </section>
    );
  };

  const renderCategoriesBlock = () => {
    const visibleCategories = categoriesHome?.length
      ? categoriesHome.map(({ category }) => category).filter(Boolean)
      : categories;

    if (!visibleCategories?.length) return null;

    return (
      <section className="container category-bubbles">
        <div className="bubbles">
          {visibleCategories.slice(0, 8).map((category) => (
            <Link
              key={category.id ?? category.code ?? category.name}
              to={`/productos/${category.code}`}
              className="bubble"
              aria-label={category.name}
            >
              <img src={category.imageHome || category.image} alt={category.name} loading="lazy" decoding="async" />
              <span>{category.name}</span>
            </Link>
          ))}
        </div>
      </section>
    );
  };

  const renderBannerBlock = (index) => (
    cartel[index] ? (
      <section className="banner-bleed">
        <picture>
          <source media="(max-width: 768px)" srcSet={getBannerImageMobile(cartel[index])} />
          <img className="banner-img" src={getBannerImage(cartel[index])} alt="" loading="lazy" decoding="async" />
        </picture>
        <Link className="banner-cta" to={redirectCartel(index)}>Ver colección</Link>
      </section>
    ) : null
  );

  const renderProductRail = (products, label, link = '/productos') => (
    !!products?.length && (
      <section className="container rail">
        <header className="hm-rail__head">
          <h2>{label}</h2>
          <Link to={link} className="hm-rail__link">Ver todo</Link>
        </header>
        {disableProductCarousel ? (
          <ProductsGrid items={products} renderItem={renderItem} ariaLabel={label} layout={productsMode} limit={productLimit} />
        ) : (
          <RowCarousel items={products} renderItem={renderItem} ariaLabel={label} />
        )}
      </section>
    )
  );

  const renderMarcasBlock = () => {
    const brandCategories = (marcasHome || [])
      .map((brand) => brand?.category || brand)
      .filter(Boolean);

    if (!brandCategories.length) return null;

    return (
      <section className="container category-bubbles">
        <div className="bubbles">
          {brandCategories.slice(0, 8).map((category) => (
            <Link
              key={category.id ?? category.code ?? category.name}
              to={`/productos/${category.code}`}
              className="bubble"
              aria-label={category.name}
            >
              <img src={category.imageHome || category.image} alt={category.name} loading="lazy" decoding="async" />
              <span>{category.name}</span>
            </Link>
          ))}
        </div>
      </section>
    );
  };

  const dynamicBlocks = {
    carrusel: renderHeroBlock,
    categorias: renderCategoriesBlock,
    marcas: renderMarcasBlock,
    'productos-destacados': () => renderProductRail(featuredProducts, 'Productos destacados'),
    'mas-vendidos': () => renderProductRail(mostSelled, 'Más vendidos'),
    ofertas: () => renderProductRail(offers, 'Ofertas', '/ofertas'),
    'banner-1': () => renderBannerBlock(0),
    'banner-2': () => renderBannerBlock(1),
    'banner-3': () => renderBannerBlock(2),
    'banner-4': () => renderBannerBlock(3),
    newsletter: () => (
      <MarketingNewsletter
        email={newsletter}
        onEmailChange={setNewsletter}
        onSubmit={submitNewsletter}
        loading={loadingNewsletter}
        clientName={client?.name_show || client?.name}
        variant={variant || 'modern'}
      />
    ),
  };

  if (customHtml?.enabled && customHtml?.mode === 'fullHome' && customHtml?.fullHome) {
    return (
      <div className={`home home-textil home-d5 ${variant ? `home-d5--${variant}` : ''} home-d5--custom-full`.trim()}>
        <CustomHtmlStyles settings={customHtml} />
        <CustomHtmlSection settings={customHtml} section="fullHome" dynamicBlocks={dynamicBlocks} />
        {dynamicBlocks.newsletter()}
      </div>
    );
  }

  return (
    <div className={`home home-textil home-d5 ${variant ? `home-d5--${variant}` : ''}`.trim()}>
      <CustomHtmlStyles settings={customHtml} />
      <CustomHtmlSection settings={customHtml} section="beforeHero" dynamicBlocks={dynamicBlocks} />
      {/* HERO */}
      {renderHeroBlock()}

      {/* CATEGORÍAS DESTACADAS */}
      <CustomHtmlSection settings={customHtml} section="afterHero" dynamicBlocks={dynamicBlocks} />

      {!!categoriesHome?.length && (
        <section className="container category-bubbles">
          <div className="bubbles">
            {categoriesHome.slice(0, 4).map(({ category }) => (
              <Link
                key={category.id}
                to={`/productos/${category.code}`}
                className="bubble"
                aria-label={category.name}
              >
                <img src={category.imageHome} alt={category.name} loading="lazy" decoding="async" />
                <span>{category.name}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* BANNERS FULL-WIDTH */}
      {cartel[0] && (
        <section className="banner-bleed">
          <picture>
            <source media="(max-width: 768px)" srcSet={getBannerImageMobile(cartel[0])} />
            <img className="banner-img" src={getBannerImage(cartel[0])} alt="" loading="lazy" decoding="async" />
          </picture>
          <Link className="banner-cta" to={redirectCartel(0)}>Ver colección</Link>
        </section>
      )}

      <CustomHtmlSection settings={customHtml} section="beforeProducts" dynamicBlocks={dynamicBlocks} />

      {!!mostSelled?.length && (
        <section className="container rail rail--first-products">
          {disableProductCarousel ? (
            <ProductsGrid items={mostSelled} renderItem={renderItem} ariaLabel="Más vendidos" layout={productsMode} limit={productLimit} />
          ) : (
            <RowCarousel items={mostSelled} renderItem={renderItem} ariaLabel="Más vendidos" />
          )}
        </section>
      )}

      {cartel[1] && (
        <section className="banner-bleed">
          <picture>
            <source media="(max-width: 768px)" srcSet={getBannerImageMobile(cartel[1])} />
            <img className="banner-img" src={getBannerImage(cartel[1])} alt="" loading="lazy" decoding="async" />
          </picture>
          <Link className="banner-cta" to={redirectCartel(1)}>Ver colección</Link>
        </section>
      )}

      {!!featuredProducts?.length && (
        <section className="container rail rail--second-products">
          {disableProductCarousel ? (
            <ProductsGrid items={featuredProducts} renderItem={renderItem} ariaLabel="Destacados" layout={productsMode} limit={productLimit} />
          ) : (
            <RowCarousel items={featuredProducts} renderItem={renderItem} ariaLabel="Destacados" />
          )}
        </section>
      )}

      {cartel[2] && (
        <section className="banner-bleed">
          <picture>
            <source media="(max-width: 768px)" srcSet={getBannerImageMobile(cartel[2])} />
            <img className="banner-img" src={getBannerImage(cartel[2])} alt="" loading="lazy" decoding="async" />
          </picture>
          <Link className="banner-cta" to={redirectCartel(2)}>Ver colección</Link>
        </section>
      )}

      {cartel[3] && (
        <section className="banner-bleed">
          <picture>
            <source media="(max-width: 768px)" srcSet={getBannerImageMobile(cartel[3])} />
            <img className="banner-img" src={getBannerImage(cartel[3])} alt="" loading="lazy" decoding="async" />
          </picture>
          <Link className="banner-cta" to={redirectCartel(3)}>Ver colección</Link>
        </section>
      )}
      {dynamicBlocks.newsletter()}
    </div>
  );
};

export default HomeD5;
